import { getUnidades, getUnidadeById, postUnidade } from "@/services/unidade";
import { Button, ConfigProvider, Input, Space, Switch, Table, TableProps, message, theme } from "antd";
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mascaraCnpj } from "@/utils/masks";
import { usePermissions } from "@/hooks/usePermissions";

export default function ConsultaUnidade() {
  const [unidades, setUnidades] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    handleGetUnidades();
  }, []);

  const handleGetUnidades = async () => {
    const response: any = await getUnidades();
    if (response.status === 200) {
      // Add local 'ativo' state for toggling enable/disable
      setUnidades(
        response.data.map((u: any) => ({
          ...u,
          ativo: u.ativo ?? true,
          disponivelSite: u.disponivelSite ?? false,
        }))
      );
    }
  };

  const handleAdd = useCallback(() => {
    navigate("/unidade/cadastro");
  }, [navigate]);

  const handleEdit = (id: string) => {
    navigate(`/unidade/${id}`);
  };

  /**
   * Alterna um booleano da unidade pela lista. Busca a unidade inteira antes de
   * salvar porque a listagem só traz alguns campos, e o SaveUnidade sobrescreve
   * tudo o que recebe — repostar o registro parcial apagaria o resto.
   */
  const alternarFlag = async (
    id: string,
    campo: "ativo" | "disponivelSite",
    mensagem: (novoValor: boolean) => string
  ) => {
    try {
      const { data: unidade } = await getUnidadeById(id);
      const novoValor = !unidade[campo];
      const response = await postUnidade({ ...unidade, [campo]: novoValor });

      if (response.status === 200 || response.status === 201) {
        setUnidades((prev) =>
          prev.map((u) => (u.id === id ? { ...u, [campo]: novoValor } : u))
        );
        message.success(mensagem(novoValor));
      } else {
        message.error("Falha ao atualizar a unidade.");
      }
    } catch (error) {
      message.error("Erro ao comunicar com o servidor.");
    }
  };

  const toggleAtivo = (id: string) =>
    alternarFlag(id, "ativo", (ativo) =>
      `Unidade ${ativo ? "ativada" : "inativada"} com sucesso!`
    );

  const toggleDisponivelSite = (id: string) =>
    alternarFlag(id, "disponivelSite", (disponivel) =>
      disponivel
        ? "Unidade passou a aparecer no site público."
        : "Unidade saiu do site público."
    );

  const columns: TableProps<any>["columns"] = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Cnpj",
      dataIndex: "cnpj",
      key: "cnpj",
      render: (value) => value ? mascaraCnpj(value) : "-"
    },
    {
      title: "Fator de Ajuste Financeiro",
      dataIndex: "fatorAjusteFinanceiro",
      key: "fatorAjusteFinanceiro",
      render: (value) =>
        value != null && value !== ""
          ? Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : "-",
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value, record) => (
        <Switch
          checked={!!value}
          onChange={() => toggleAtivo(record.id)}
          checkedChildren="Ativo"
          unCheckedChildren="Inativo"
        />
      ),
    },
    {
      title: "Site público",
      dataIndex: "disponivelSite",
      key: "disponivelSite",
      render: (value, record) => (
        <Switch
          checked={!!value}
          onChange={() => toggleDisponivelSite(record.id)}
          checkedChildren="Sim"
          unCheckedChildren="Não"
        />
      ),
    },
    {
      title: "Ações",
      key: "acoes",
      render: (_, record) => (
        <Space size="middle">
          {canEdit(6) && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  const filteredUnidades = unidades.filter((u) =>
    u.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.cnpj?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Unidades</span>
      </div>
      <ConfigProvider
        theme={{
          algorithm: [theme.compactAlgorithm, theme.darkAlgorithm],
        }}
      >
        <div className="flex gap-24">
          <Input
            type="text"
            placeholder="Pesquisar por nome ou CNPJ"
            className="w-96 mb-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {canInsert(6) && (
            <Button className="flex gap-2 items-center" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </Button>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredUnidades} rowKey="id" />
          </div>
        </div>
      </ConfigProvider>
    </>
  );
} 